import {
  AlignCenterIcon,
  AlignJustifyIcon,
  AlignLeftIcon,
  AlignRightIcon,
  BoldIcon,
  ItalicsIcon,
  LinkAngledIcon,
  UnderlineIcon,
} from '@shared/assets/icons';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import React, { useEffect, useReducer, useRef, useState } from 'react';
import { Editor, useEditorState } from '@tiptap/react';
import { BubbleMenuStyles } from '@shared/ui/tiptap';
import { StylesText } from '@entities/leadCollector';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';

interface ToolbarProps {
  editor: Editor;
  properties?: unknown;
  hiddenStyles?: string[];
}

export const Toolbar = ({ editor, properties, hiddenStyles }: ToolbarProps) => {
  const [error, setError] = useState('');
  const [bubbleMenuStyles, setBubbleMenuStyles] = useState<BubbleMenuStyles>({});
  const bubbleMenuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isPasteLinked = useRef<boolean>(false);
  const [, forceUpdate] = useReducer((tick: number) => tick + 1, 0);

  const onChangeStyles = (style: keyof BubbleMenuStyles, value: boolean) => {
    setBubbleMenuStyles((prevBubbleMenuStyles) => {
      return { ...prevBubbleMenuStyles, [style]: value };
    });
  };

  useEffect(() => {
    if (!properties || typeof properties !== 'object') return;

    onChangeStyles(StylesText.LINK, StylesText.LINK in properties && !hiddenStyles?.includes(StylesText.LINK));

    const styles = 'styles' in properties ? (properties.styles as string[]) : [];
    onChangeStyles(StylesText.BOLD, styles.includes(StylesText.BOLD) && !hiddenStyles?.includes(StylesText.BOLD));
    onChangeStyles(StylesText.ITALIC, styles.includes(StylesText.ITALIC) && !hiddenStyles?.includes(StylesText.ITALIC));
    onChangeStyles(
      StylesText.UNDERLINE,
      styles.includes(StylesText.UNDERLINE) && !hiddenStyles?.includes(StylesText.UNDERLINE)
    );

    const alignments = 'alignment' in properties ? (properties.alignment as string[]) : [];
    onChangeStyles(StylesText.LEFT, alignments.includes(StylesText.LEFT) && !hiddenStyles?.includes(StylesText.LEFT));
    onChangeStyles(
      StylesText.CENTER,
      alignments.includes(StylesText.CENTER) && !hiddenStyles?.includes(StylesText.CENTER)
    );
    onChangeStyles(
      StylesText.RIGHT,
      alignments.includes(StylesText.RIGHT) && !hiddenStyles?.includes(StylesText.RIGHT)
    );
    onChangeStyles(
      StylesText.JUSTIFY,
      alignments.includes(StylesText.JUSTIFY) && !hiddenStyles?.includes(StylesText.JUSTIFY)
    );
  }, [properties]);

  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        isBold: ctx.editor.isActive(StylesText.BOLD) ?? false,
        isItalic: ctx.editor.isActive(StylesText.ITALIC) ?? false,
        isUnderline: ctx.editor.isActive(StylesText.UNDERLINE) ?? false,
        isLink: ctx.editor.isActive(StylesText.LINK) ?? false,
        isLeft: ctx.editor.isActive({ textAlign: StylesText.LEFT }) ?? false,
        isCenter: ctx.editor.isActive({ textAlign: StylesText.CENTER }) ?? false,
        isRight: ctx.editor.isActive({ textAlign: StylesText.RIGHT }) ?? false,
        isJustify: ctx.editor.isActive({ textAlign: StylesText.JUSTIFY }) ?? false,
      };
    },
  });

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (bubbleMenuRef.current && !bubbleMenuRef.current.contains(event.target as Node)) {
        handleSaveLink();
      }
    };

    document.addEventListener('mousedown', handleClick);

    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  const onPressEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveLink();
    }
  };

  const focusWithoutScroll = () => editor.chain().focus(undefined, { scrollIntoView: false });

  const handleSaveLink = () => {
    if (!inputRef.current?.value && isPasteLinked.current) {
      focusWithoutScroll().run();
      isPasteLinked.current = false;
      setError('');
      forceUpdate();
      return;
    }

    try {
      if (!inputRef.current?.value) {
        return;
      }

      const { protocol } = new URL(inputRef.current?.value || '');
      const isValidLink = protocol === 'http:' || protocol === 'https:';

      if (!isValidLink) {
        setError('Неправильный формат ссылки');
        return;
      }

      setError('');
      focusWithoutScroll().setLink({ href: inputRef.current.value }).focus(undefined, { scrollIntoView: false }).run();
      inputRef.current.value = '';
      isPasteLinked.current = false;
      forceUpdate();
    } catch {
      setError('Неправильный формат ссылки');
    }
  };

  const handleAddLink = () => {
    if (!editorState.isLink) {
      isPasteLinked.current = true;
      forceUpdate();
      requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
      return;
    }

    focusWithoutScroll().unsetLink().run();
  };

  const preventButtonFocus = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  useEffect(() => {
    setError('');
  }, [isPasteLinked]);

  const isEditable = editor.isEditable;

  return (
    <div ref={bubbleMenuRef} {...stylex.props(styles.bubbleMenu)}>
      {isPasteLinked.current && (
        <div {...stylex.props(styles.linkContainer)}>
          <input
            {...stylex.props(styles.link, !!error && styles.linkError)}
            ref={inputRef}
            placeholder="Вставьте ссылку"
            onKeyDown={onPressEnter}
            onClick={(e) => e.stopPropagation()}
          />
          {error && <span {...stylex.props(styles.linkErrorText)}>{error}</span>}
        </div>
      )}
      {bubbleMenuStyles.bold && (
        <UnstyledButton
          disabled={!isEditable}
          onMouseDown={preventButtonFocus}
          onClick={() => focusWithoutScroll().toggleBold().run()}
        >
          <BoldIcon {...stylex.props(isEditable && editorState.isBold && styles.menuActive)} color="currentColor" />
        </UnstyledButton>
      )}

      {bubbleMenuStyles.italic && (
        <UnstyledButton
          disabled={!isEditable}
          onMouseDown={preventButtonFocus}
          onClick={() => focusWithoutScroll().toggleItalic().run()}
        >
          <ItalicsIcon {...stylex.props(isEditable && editorState.isItalic && styles.menuActive)} />
        </UnstyledButton>
      )}

      {bubbleMenuStyles.underline && (
        <UnstyledButton
          disabled={!isEditable}
          onMouseDown={preventButtonFocus}
          onClick={() => focusWithoutScroll().toggleUnderline().run()}
        >
          <UnderlineIcon {...stylex.props(isEditable && editorState.isUnderline && styles.menuActive)} />
        </UnstyledButton>
      )}

      {bubbleMenuStyles.link && (
        <UnstyledButton disabled={!isEditable} onMouseDown={preventButtonFocus} onClick={handleAddLink}>
          <LinkAngledIcon {...stylex.props(isEditable && editorState.isLink && styles.menuActive)} />
        </UnstyledButton>
      )}

      {bubbleMenuStyles.left && (
        <UnstyledButton
          disabled={!isEditable}
          onMouseDown={preventButtonFocus}
          onClick={() => focusWithoutScroll().setTextAlign('left').run()}
        >
          <AlignLeftIcon {...stylex.props(isEditable && editorState.isLeft && styles.menuActive)} />
        </UnstyledButton>
      )}

      {bubbleMenuStyles.center && (
        <UnstyledButton
          disabled={!isEditable}
          onMouseDown={preventButtonFocus}
          onClick={() => focusWithoutScroll().setTextAlign('center').run()}
        >
          <AlignCenterIcon {...stylex.props(isEditable && editorState.isCenter && styles.menuActive)} />
        </UnstyledButton>
      )}

      {bubbleMenuStyles.right && (
        <UnstyledButton
          disabled={!isEditable}
          onMouseDown={preventButtonFocus}
          onClick={() => focusWithoutScroll().setTextAlign('right').run()}
        >
          <AlignRightIcon {...stylex.props(isEditable && editorState.isRight && styles.menuActive)} />
        </UnstyledButton>
      )}

      {bubbleMenuStyles.justify && (
        <UnstyledButton
          disabled={!isEditable}
          onMouseDown={preventButtonFocus}
          onClick={() => focusWithoutScroll().setTextAlign('justify').run()}
        >
          <AlignJustifyIcon {...stylex.props(isEditable && editorState.isJustify && styles.menuActive)} />
        </UnstyledButton>
      )}
    </div>
  );
};
